inherit("Modules/Card", {
	parent: `Module`,
	requires: [`XHR`],
	defaults: {
		orientation: `FLYER`, // Accepts FLYER/PORTRAIT, MEDIUM, COMPACT/LANDSCAPE, WIDE (Collection single column grid)
		mobileOrientation: `FLYER`, // Accepts FLYER/PORTRAIT, MEDIUM, COMPACT/LANDSCAPE, WIDE (Collection single column grid)
		dynamicallySizeOnDesktop: false, // If true and desktop, the container will match the aspect ratio of the first image
		dynamicallySizeOnMobile: false, // If true and mobile, the container will match the aspect ratio of the first image
		imageMode: `advanced`, // simple | advanced
		infoOnHover: false, // Whether or not to display the info circle on hover (upper-right corner)
		item: null, // required
		displayOverlay: false, // Whether or not to gray-out the image
		expandImageOnHover: true,
		colorHighlight: null, // Color of the highlight on the image. This is the vertical left bar for reserved tickets
		displayInfoAboveImageMobile: false, // Whether or not to display the info above the image vertically
		moreInfoHidden: false, // Whether or not the more info button is hidden when hovering
		version: `legacy`, // legacy | ux4
		render: true, // render() before constructor
	},
	prepare(setup) {
		const { data } = this;
		const {
			colorHighlight,
			dynamicallySizeOnDesktop,
			dynamicallySizeOnMobile,
			expandImageOnHover,
			imageMode,
			infoOnHover,
			item,
			mobileOrientation,
			orientation,
			displayInfoAboveImageMobile,
			moreInfoHidden,
			version,
		} = setup;

		this.defaults(item, {
			badges: null, // object with topLeft, topRight, bottomLeft, bottomRight. Each badge has text and color.
			blurb: null,
			blurbSecondary: null,
			footnote: null,
			description: null, // Description will only appear for the WIDE orientation
			externalUrl: null, // Link that the more info button will go to. If no link is given, an action of "select" will be attached to the <a> tag
			hoverMessage: `More Info`,
			imageUrls: [],
			title: null,
		});

		const { badges, blurb, blurbSecondary, description, externalUrl, footnote, hoverMessage, imageUrls, title } = item;

		if (imageUrls.length === 0 && this.isString(item.imageUrl)) {
			imageUrls.push(item.imageUrl);
		}

		this.addClass(`version-${version}`);

		this.assign(data, {
			badges,
			colorHighlight,
			currentImageIndex: 0,
			dynamicallySizeOnDesktop,
			dynamicallySizeOnMobile,
			description,
			expandImageOnHover,
			footnote,
			hoverMessage,
			imageMode,
			imageUrls,
			infoOnHover,
			isUnseen: true,
			link: externalUrl,
			loaded: false,
			mobileOrientation,
			orientation,
			subtitle: blurb,
			subtitleSecondary: blurbSecondary,
			title,
			displayInfoAboveImageMobile,
			moreInfoHidden,
			version,
		});
	},
	constructor(setup) {
		const { data, callbacks } = this;
		const { imageMode, imageUrls } = data;
		const imageContainer = this.get(`.image-container`);

		if (!this.isArray(imageUrls, 1) || !this.isString(imageUrls[0], 1)) {
			this.addClass(`no-image`, imageContainer);
		}

		this.updateProperties();

		if (imageMode === `advanced`) {
			if (imageUrls.length > 1) {
				this.createImageDotHelper();
			}

			return this.when({
				promise: this.create({
					namespace: `Modules/SwipableImageGroup`,
					setup: {
						name: `swipable-image-container`,
						imageUrls,
						lazyLoadImages: true,
						onClick: callbacks.select,
						onImageSelectionChange: this.onImageSelectionChange,
					},
				}),
				then: () => {
					this.dynamicallyUpdateImageAspectRatio();
				},
			});
		} else {
			this.dynamicallyUpdateImageAspectRatio();
		}
	},

	ready(setup) {
		const { App, data } = this;
		const { imageMode } = data;

		if (imageMode === `simple`) {
			data.isUnseen = true;

			// handle lazy loading
			App.onScroll(this.checkLazyLoad);
			App.onTick(this.checkLazyLoad);
		}
	},

	api: {
		checkLazyLoad: {
			throttle: 500,
			value() {
				const { App, XHR, data, dimensions } = this;
				const { imageMode, imageUrls, isUnseen } = data;
				const [src] = imageUrls;

				if (imageMode === `simple` && isUnseen && dimensions.isVisible && dimensions.inViewport) {
					const background = this.get(`.background`);

					App.offScroll(this.checkLazyLoad);
					App.offTick(this.checkLazyLoad);
					data.isUnseen = false;

					if (this.isString(src, 1)) {
						XHR.loadImage({
							src,
							then: () => {
								background.style.backgroundImage = `url("${src}")`;
								this.removeClass(`unseen`, background);
							},
						});
					} else {
						this.removeClass(`unseen`, background);
					}
				}
			},
		},
		createImageDotHelper: {
			value() {
				const { data } = this;
				const { imageUrls } = data;
				const dotHelperElement = this.get(`.image-dot-helper`);

				this.each(imageUrls, (imageUrl, index) => {
					const dotElement = document.createElement(`div`);

					this.addClass(`dot`, dotElement);
					if (index === 0) {
						this.addClass(`active`, dotElement);
					}

					dotHelperElement.appendChild(dotElement);
				});
			},
		},

		updateItem: {
			public: true,
			value(context, item) {
				this.each(item, (value, key) => {
					if (key === `blurb`) {
						const target = this.get(`.info .subtitle`);

						if (this.isString(value, 1)) {
							target.innerHTML = value;
							this.show({ target });
						} else {
							this.hide({ target });
						}
					}

					if (key === `blurbSecondary`) {
						const target = this.get(`.info .subtitleSecondary`);

						if (this.isString(value, 1)) {
							target.innerHTML = value;
							this.show({ target });
						} else {
							this.hide({ target });
						}
					}

					if (key === `footnote`) {
						const target = this.get(`.info .footnote`);

						if (this.isString(value, 1)) {
							target.innerHTML = value;
							this.show({ target });
						} else {
							this.hide({ target });
						}
					}
				});

				this.dynamicallyUpdateImageAspectRatio();
			},
		},

		updateProperties: {
			public: true,
			value(context, properties) {
				const { data } = this;
				const { badges, colorHighlight, displayOverlay } = this.isObject(properties) ? properties : data;
				const imageContainer = this.get(`.image-container`);
				const colorHighlightElement = this.get(`[name="color-highlight"]`);

				this.assign(data, { badges, displayOverlay });
				this.updateBadges();

				if (displayOverlay) {
					this.show({ target: this.get(`.overlay`) });
				} else {
					this.hide({ target: this.get(`.overlay`) });
				}

				if (colorHighlight != null) {
					this.addClass(`color-highlight`, imageContainer);
					colorHighlightElement.style.backgroundColor = colorHighlight;
				} else {
					this.removeClass(`color-highlight`, imageContainer);
					colorHighlightElement.style.backgroundColor = ``;
				}
			},
		},

		updateBadges: {
			value() {
				const { data } = this;
				const { badges } = data;

				if (this.isObject(badges)) {
					const { topLeft, topRight, bottomLeft, bottomRight } = badges;
					const topLeftSectionContainer = this.get(`.badge-section.top-left`);
					const topRightSectionContainer = this.get(`.badge-section.top-right`);
					const bottomLeftSectionContainer = this.get(`.badge-section.bottom-left`);
					const bottomRightSectionContainer = this.get(`.badge-section.bottom-right`);

					[
						{ badge: topLeft, target: topLeftSectionContainer },
						{ badge: topRight, target: topRightSectionContainer },
						{ badge: bottomLeft, target: bottomLeftSectionContainer },
						{ badge: bottomRight, target: bottomRightSectionContainer },
					].forEach(({ badge, target }) => {
						if (this.isArray(badge, 1)) {
							this.updateBadge(badge, target);
							this.removeClass(`hidden`, target);
						} else if (this.isObject(badge) && !this.isArray(badge)) {
							this.updateBadge([badge], target);
							this.removeClass(`hidden`, target);
						} else {
							this.addClass(`hidden`, target);
						}
					});
				}
			},
		},

		updateBadge: {
			value(badge, target) {
				const { DOM, Templates } = this;
				const children = target.children;

				this.each(badge, (badge, index) => {
					let child = children[index];
					this.defaults(badge, {
						size: `large`,
					});
					const { color, icon, size, text } = badge;

					if (!this.isObject(child)) {
						child = document.createElement(`div`);
						this.addClass(`badge card-badge`, child);
						target.appendChild(child);
					}
					child.innerHTML = `${this.isString(icon, 1) ? Templates.getIcon(icon) + text : text}`;
					this.removeClass(`gray black red blue green dark-green blur small resale`, child); // remove all colors
					this.addClass(color, child);

					// removing any prior click listeners on the target
					DOM.off({
						event: `click`,
						target: child,
					});

					if (badge.isClickable) {
						this.addClass(`clickable`, child);

						DOM.on({
							event: `click`,
							target: child,
							handler: (evt) => {
								evt.stopPropagation();

								badge.onClick();
							},
						});
					} else {
						this.removeClass(`clickable`, child);
					}

					if (size === `small`) {
						this.addClass(`small`, child);
					} else {
						// Shrink font size if necessary
						DOM.deferUICalculations(this.container, () => {
							if (DOM.getDimensions(child).width > DOM.getDimensions(this.container).width) {
								this.addClass(`small`, child);
							}
						});
					}
				});

				if (children.length > badge.length) {
					this.each(children, (child, index) => {
						if (index >= badge.length) {
							target.removeChild(child);
						}
					});
				}
			},
		},

		onImageSelectionChange: {
			value(newImageIndex) {
				const dotElements = this.select(`.dot`);
				const activeDot = dotElements[newImageIndex];

				this.removeClass(`active`, dotElements);
				this.addClass(`active`, activeDot);
			},
		},

		// Todo: clean up this function. Was added to multiple times when being rushed for a realease.
		dynamicallyUpdateImageAspectRatio: {
			value() {
				const { data, instances, App, DOM, XHR } = this;
				const { imageUrls, dynamicallySizeOnDesktop, dynamicallySizeOnMobile } = data;
				const imageContainer = this.get(`.image-container`);
				const descriptionContainer = this.get(`.description`);
				const swipableImageGroupTarget =
					instances[`swipable-image-container`] != null ? instances[`swipable-image-container`].container : null;
				const coverClass = `background-size-cover`;
				const containClass = `background-size-contain`;
				const isDynamicSizingMobile =
					dynamicallySizeOnMobile && App.isMobileViewportSize() && this.isArray(imageUrls, 1);
				const isDynamicSizingDesktop =
					dynamicallySizeOnDesktop && !App.isMobileViewportSize() && this.isArray(imageUrls, 1);
				const titleDimensions = DOM.getDimensions(this.get(`.title`));
				const descriptionDimensions = DOM.getDimensions(this.get(`.description`));
				const currentFontSize = parseFloat(window.getComputedStyle(document.documentElement).fontSize);
				const moreInfoHeightPlusMargin = 4.14 * currentFontSize; // the height of the more info button + margin

				if (isDynamicSizingMobile || isDynamicSizingDesktop) {
					if (this.isArray(imageUrls, 1) && imageUrls[0] != null) {
						// incase there is no image
						XHR.loadImage({
							src: imageUrls[0],
							then: (newImage) => {
								const newImageAspectRatio = newImage.naturalWidth / newImage.naturalHeight;

								if (swipableImageGroupTarget != null) {
									this.addClass(containClass, swipableImageGroupTarget);
									this.removeClass(coverClass, swipableImageGroupTarget);
								}

								if (imageContainer != null) {
									imageContainer.style.aspectRatio = newImageAspectRatio;

									if (App.isMobileViewportSize()) {
										if (descriptionContainer != null) {
											descriptionContainer.style.height = `auto`;
											descriptionContainer.style.webkitLineClamp = null;
										}
									} else {
										DOM.deferUICalculations(imageContainer, () => {
											if (!App.isMobileViewportSize()) {
												const remsRestOfInfo =
													(descriptionDimensions.top - titleDimensions.top + moreInfoHeightPlusMargin) /
													currentFontSize;

												if (descriptionContainer != null) {
													let heightRems =
														DOM.getDimensions(imageContainer).width / newImageAspectRatio / currentFontSize -
														remsRestOfInfo;
													const lineHeightRems = 1.6644; // Chage if the line height changes. Currently mobile and desktop are the same
													const lines = Math.floor(heightRems / lineHeightRems);
													heightRems = lines * lineHeightRems;
													const lineToClamp = lines;

													descriptionContainer.style.height = `${heightRems}rem`;
													descriptionContainer.style.webkitLineClamp = lineToClamp;
												}
												this.updateMoreInfoButton();
											}
										});
									}
								}
							},
						});
					} else {
						const defaultAspectRatio = 1.5;

						if (App.isMobileViewportSize()) {
							if (descriptionContainer != null) {
								descriptionContainer.style.height = `auto`;
								descriptionContainer.style.webkitLineClamp = null;
							}
						} else {
							DOM.deferUICalculations(imageContainer, () => {
								const remsRestOfInfo =
									(descriptionDimensions.top - titleDimensions.top + moreInfoHeightPlusMargin) / currentFontSize;

								if (descriptionContainer != null) {
									let heightRems =
										DOM.getDimensions(imageContainer).width / defaultAspectRatio / currentFontSize - remsRestOfInfo;
									const lineHeightRems = 1.6644; // Chage if the line height changes. Currently mobile and desktop are the same
									const lines = Math.floor(heightRems / lineHeightRems);
									heightRems = lines * lineHeightRems;
									const lineToClamp = lines;
									descriptionContainer.style.height = `${heightRems}rem`;
									descriptionContainer.style.webkitLineClamp = lineToClamp;
								}
								this.updateMoreInfoButton();
							});
						}
					}
				} else {
					if (swipableImageGroupTarget != null) {
						this.addClass(coverClass, swipableImageGroupTarget);
						this.removeClass(containClass, swipableImageGroupTarget);
					}
				}
			},
		},

		updateMoreInfoButton: {
			async value() {
				const descriptionWrapper = this.get(`.description`);
				const moreInfoElement = this.get(`.more-info-button`);
				const transition = `fade`;

				if (this.isElement(descriptionWrapper)) {
					if (this.isElement(moreInfoElement)) {
						await Promise.allSettled(descriptionWrapper.getAnimations({ subtree: true }).map((a) => a.finished));
						// make sure animations are done before measuring height

						const { clientHeight, scrollHeight } = descriptionWrapper;
						if (scrollHeight > clientHeight) {
							this.addClass(`overflowing`, descriptionWrapper);
							this.show({ target: moreInfoElement, transition });
						} else {
							this.hide({ target: moreInfoElement, transition });
						}
					}
				} else {
					if (this.isElement(moreInfoElement)) {
						this.hide({ target: moreInfoElement, transition });
					}
				}
			},
		},
	},

	listeners: [
		{
			trigger: `visible`,
			handler() {
				this.updateMoreInfoButton();
				this.checkLazyLoad();
			},
		},
		{
			event: `resize`,
			target: window,
			matchContainer: false,
			throttle: 200,
			handler() {
				this.updateBadges();
			},
		},
		{
			event: `click`,
			filter: `[action]`,
			handler(evt) {
				const { callbacks } = this;
				const { currentTarget: link } = evt;
				const action = link.getAttribute(`action`);

				switch (action) {
					case `select`: {
						if (this.isFunction(callbacks.select)) {
							callbacks.select();
						}
						break;
					}
				}
			},
		},
		{
			event: `resize`,
			target: window,
			debounce: 200,
			handler() {
				this.dynamicallyUpdateImageAspectRatio();
			},
		},
	],
});

define("Modules/Card.templates",["handlebars"], function(Handlebars) {

this["Templates"] = this["Templates"] || {};

this["Templates"]["Modules/Card/main"] = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return " expandImageOnHover";
},"3":function(container,depth0,helpers,partials,data) {
    return " display-info-above-mobile";
},"5":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"background unseen\"> "
    + container.escapeExpression(lookupProperty(helpers,"getIcon").call(depth0 != null ? depth0 : (container.nullContext || {}),"tixr",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":355},"end":{"line":1,"column":373}}}))
    + " </div> ";
},"7":function(container,depth0,helpers,partials,data) {
    return " <div name=\"swipable-image-container\"></div> ";
},"9":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <a class=\"more-info"
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"moreInfoHidden") : depth0),{"name":"if","hash":{},"fn":container.program(10, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":944},"end":{"line":1,"column":980}}})) != null ? stack1 : "")
    + "\" "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"link") : depth0),{"name":"if","hash":{},"fn":container.program(12, data, 0),"inverse":container.program(14, data, 0),"data":data,"loc":{"start":{"line":1,"column":982},"end":{"line":1,"column":1048}}})) != null ? stack1 : "")
    + "></a> ";
},"10":function(container,depth0,helpers,partials,data) {
    return " hidden";
},"12":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "href=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"link") : depth0), depth0))
    + "\"";
},"14":function(container,depth0,helpers,partials,data) {
    return "href=\"#\" action=\"select\"";
},"16":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + ((stack1 = lookupProperty(helpers,"compare").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"imageUrls") : depth0)) != null ? lookupProperty(stack1,"length") : stack1),1,">",{"name":"compare","hash":{},"fn":container.program(17, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":1101},"end":{"line":1,"column":1186}}})) != null ? stack1 : "")
    + " ";
},"17":function(container,depth0,helpers,partials,data) {
    return " <div class=\"image-dot-helper\"></div> ";
},"19":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div name=\"more-info-icon\" class=\"hide-mobile\">"
    + container.escapeExpression(lookupProperty(helpers,"getIcon").call(depth0 != null ? depth0 : (container.nullContext || {}),"info-i",{"name":"getIcon","hash":{},"data":data,"loc":{"start":{"line":1,"column":1267},"end":{"line":1,"column":1287}}}))
    + "</div> ";
},"21":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=depth0 != null ? depth0 : (container.nullContext || {}), alias2=container.lambda, alias3=container.escapeExpression, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"description") : depth0),{"name":"if","hash":{},"fn":container.program(22, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":1597},"end":{"line":1,"column":1671}}})) != null ? stack1 : "")
    + " <a class=\"more-info-button ux4 button tertiary small hide-desktop\" theme=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"theme") : depth0), depth0))
    + "\" "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"link") : depth0),{"name":"if","hash":{},"fn":container.program(12, data, 0),"inverse":container.program(14, data, 0),"data":data,"loc":{"start":{"line":1,"column":1757},"end":{"line":1,"column":1823}}})) != null ? stack1 : "")
    + ">"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"hoverMessage") : depth0), depth0))
    + "</a> <a class=\"more-info-button ux4 button tertiary medium hide-mobile\" theme=\""
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"theme") : depth0), depth0))
    + "\" "
    + ((stack1 = lookupProperty(helpers,"if").call(alias1,(depth0 != null ? lookupProperty(depth0,"link") : depth0),{"name":"if","hash":{},"fn":container.program(12, data, 0),"inverse":container.program(14, data, 0),"data":data,"loc":{"start":{"line":1,"column":1930},"end":{"line":1,"column":1996}}})) != null ? stack1 : "")
    + ">"
    + alias3(alias2((depth0 != null ? lookupProperty(depth0,"hoverMessage") : depth0), depth0))
    + "</a> ";
},"22":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"description\">"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"description") : depth0), depth0)) != null ? stack1 : "")
    + "</div>";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, alias1=container.lambda, alias2=container.escapeExpression, alias3=depth0 != null ? depth0 : (container.nullContext || {}), lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <div class=\"card desktop-orientation-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"orientation") : depth0), depth0))
    + " mobile-orientation-"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"mobileOrientation") : depth0), depth0))
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"expandImageOnHover") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":138},"end":{"line":1,"column":190}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"displayInfoAboveImageMobile") : depth0),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":190},"end":{"line":1,"column":258}}})) != null ? stack1 : "")
    + "\"> <div class=\"image-container\"> "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depth0 != null ? lookupProperty(depth0,"imageMode") : depth0),"simple",{"name":"compare","hash":{},"fn":container.program(5, data, 0),"inverse":container.program(7, data, 0),"data":data,"loc":{"start":{"line":1,"column":291},"end":{"line":1,"column":446}}})) != null ? stack1 : "")
    + " <div class=\"extra-content\"></div>  <div class=\"overlay\"></div> <div class=\"badge-section top-left\"></div>  <div class=\"badge-section top-right\"></div>  <div class=\"badge-section bottom-right\"></div>  <div class=\"badge-section bottom-left\"></div>  "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depth0 != null ? lookupProperty(depth0,"orientation") : depth0),"WIDE","!=",{"name":"compare","hash":{},"fn":container.program(9, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":888},"end":{"line":1,"column":1066}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depth0 != null ? lookupProperty(depth0,"imageMode") : depth0),"advanced",{"name":"compare","hash":{},"fn":container.program(16, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":1067},"end":{"line":1,"column":1199}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(alias3,(depth0 != null ? lookupProperty(depth0,"infoOnHover") : depth0),{"name":"if","hash":{},"fn":container.program(19, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":1200},"end":{"line":1,"column":1301}}})) != null ? stack1 : "")
    + " <div name=\"color-highlight\"></div> </div> <div class=\"info\"> <p class=\"title\">"
    + alias2(alias1((depth0 != null ? lookupProperty(depth0,"title") : depth0), depth0))
    + "</p> <div class=\"info-secondary\"> <p class=\"subtitle\">"
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"subtitle") : depth0), depth0)) != null ? stack1 : "")
    + "</p> <p class=\"subtitleSecondary\">"
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"subtitleSecondary") : depth0), depth0)) != null ? stack1 : "")
    + "</p> <p class=\"footnote\">"
    + ((stack1 = alias1((depth0 != null ? lookupProperty(depth0,"footnote") : depth0), depth0)) != null ? stack1 : "")
    + "</p> </div> "
    + ((stack1 = lookupProperty(helpers,"compare").call(alias3,(depth0 != null ? lookupProperty(depth0,"orientation") : depth0),"WIDE",{"name":"compare","hash":{},"fn":container.program(21, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":1,"column":1565},"end":{"line":1,"column":2030}}})) != null ? stack1 : "")
    + " </div> </div>";
},"useData":true});

return this["Templates"];

});
//# sourceMappingURL=Card.js.map